import { Controller } from "@hotwired/stimulus"
import { useClickOutside } from "../helpers/click_outside_mixin"

const DEBOUNCE_MS = 300
const FIELD_NAME = "scheduling_appointment[extra_psychologist_client_ids][]"
const PRIMARY_NAME = "scheduling_appointment[psychologist_client_id]"

// Multi-select client search for extra people in the same session.
export default class extends Controller {
  static targets = [ "input", "listbox", "menu", "chips" ]
  static values = {
    searchUrl: String,
    loadingLabel: String,
    emptyLabel: String,
    removeLabel: String
  }

  connect() {
    this._debounceTimer = null
    this._activeIndex = -1
    this._options = []
    this._fetchController = null
    this._clickOutside = useClickOutside(this.element, () => this.closeMenu())
  }

  disconnect() {
    this._clickOutside?.remove()
    clearTimeout(this._debounceTimer)
    this._fetchController?.abort()
  }

  onFocus() {
    this.fetchOptions(this.inputTarget.value)
  }

  search() {
    clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => {
      this.fetchOptions(this.inputTarget.value)
    }, DEBOUNCE_MS)
  }

  async fetchOptions(query) {
    this._fetchController?.abort()
    this._fetchController = new AbortController()

    const url = new URL(this.searchUrlValue, window.location.origin)
    const normalized = query.trim()
    if (normalized) url.searchParams.set("q", normalized)

    this.setLoading(true)
    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: this._fetchController.signal
      })
      if (!response.ok) return

      const data = await response.json()
      this._options = Array.isArray(data.clients) ? data.clients : []
      this._activeIndex = -1
      this.renderOptions()
      this.openMenu()
    } catch (error) {
      if (error.name === "AbortError") return
      this._options = []
      this.renderOptions()
      this.openMenu()
    } finally {
      this.setLoading(false)
    }
  }

  renderOptions() {
    this.listboxTarget.replaceChildren()
    const taken = this.takenIds()
    const options = this._options.filter((client) => !taken.has(String(client.id)))

    if (options.length === 0) {
      const empty = document.createElement("p")
      empty.className = "psychologist-client-search__empty"
      empty.textContent = this.emptyLabelValue
      this.listboxTarget.appendChild(empty)
      return
    }

    options.forEach((client, index) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "psychologist-client-search__option"
      button.role = "option"
      button.id = `${this.listboxTarget.id}-option-${index}`
      button.dataset.clientId = String(client.id)
      button.dataset.clientLabel = client.label
      button.dataset.action = "click->appointment-extra-clients#selectOption"
      button.setAttribute("aria-selected", "false")

      const label = document.createElement("span")
      label.className = "psychologist-client-search__option-label"
      label.textContent = client.label
      button.appendChild(label)

      if (client.meta) {
        const meta = document.createElement("span")
        meta.className = "psychologist-client-search__option-meta"
        meta.textContent = client.meta
        button.appendChild(meta)
      }

      this.listboxTarget.appendChild(button)
    })
  }

  selectOption(event) {
    const button = event.currentTarget
    this.addChip(button.dataset.clientId, button.dataset.clientLabel)
  }

  addChip(clientId, label) {
    if (!clientId || this.takenIds().has(String(clientId))) return

    const chip = document.createElement("span")
    chip.className = "appointment-extra-clients__chip"

    const hidden = document.createElement("input")
    hidden.type = "hidden"
    hidden.name = FIELD_NAME
    hidden.value = String(clientId)
    chip.appendChild(hidden)

    const name = document.createElement("span")
    name.textContent = label
    chip.appendChild(name)

    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "appointment-extra-clients__remove"
    remove.dataset.action = "click->appointment-extra-clients#remove"
    remove.setAttribute("aria-label", this.removeLabelValue.replaceAll("%{name}", label))
    remove.textContent = "×"
    chip.appendChild(remove)

    this.chipsTarget.appendChild(chip)
    this.inputTarget.value = ""
    this.closeMenu()
  }

  remove(event) {
    event.currentTarget.closest(".appointment-extra-clients__chip")?.remove()
  }

  onKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      const option = this.optionButtons()[this._activeIndex]
      if (option) this.addChip(option.dataset.clientId, option.dataset.clientLabel)
      return
    }

    if (!this.menuTarget.hidden && this.optionButtons().length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault()
        this.moveActive(1)
        return
      }
      if (event.key === "ArrowUp") {
        event.preventDefault()
        this.moveActive(-1)
        return
      }
    }

    if (event.key === "Escape") this.closeMenu()
  }

  moveActive(delta) {
    const buttons = this.optionButtons()
    if (buttons.length === 0) return

    this._activeIndex = Math.max(0, Math.min(buttons.length - 1, this._activeIndex + delta))
    buttons.forEach((button, index) => {
      const active = index === this._activeIndex
      button.classList.toggle("is-active", active)
      button.setAttribute("aria-selected", active ? "true" : "false")
      if (active) {
        this.inputTarget.setAttribute("aria-activedescendant", button.id)
        button.scrollIntoView({ block: "nearest" })
      }
    })
  }

  optionButtons() {
    return Array.from(this.listboxTarget.querySelectorAll(".psychologist-client-search__option"))
  }

  takenIds() {
    const ids = new Set(
      Array.from(this.chipsTarget.querySelectorAll("input")).map((input) => input.value).filter(Boolean)
    )
    const primary = this.element.closest("form")?.querySelector(`input[name="${PRIMARY_NAME}"]`)
    if (primary?.value) ids.add(primary.value)
    return ids
  }

  openMenu() {
    this.menuTarget.hidden = false
    this.inputTarget.setAttribute("aria-expanded", "true")
    this._clickOutside.add()
  }

  closeMenu() {
    this.menuTarget.hidden = true
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
    this._clickOutside.remove()
    this._activeIndex = -1
    this.optionButtons().forEach((button) => {
      button.classList.remove("is-active")
      button.setAttribute("aria-selected", "false")
    })
  }

  setLoading(loading) {
    this.element.classList.toggle("is-loading-options", loading)
    if (!loading) return

    this.listboxTarget.replaceChildren()
    const status = document.createElement("p")
    status.className = "psychologist-client-search__status"
    status.setAttribute("role", "status")
    status.textContent = this.loadingLabelValue
    this.listboxTarget.appendChild(status)
    this.openMenu()
  }
}
